import React, { useState, useEffect, useMemo } from 'react';
import {
    Layout, Card, Table, Button, Upload, message, Typography, Row, Col,
    Form, Modal, DatePicker, Select, Input, Tag, Statistic
} from 'antd';
import {
    UploadOutlined, ReloadOutlined, PlusOutlined,
    ArrowUpOutlined, ArrowDownOutlined, SwapOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/api';

const { Title, Text } = Typography;

const BankStatementImportPage = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [directors, setDirectors] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [isMappingModalVisible, setIsMappingModalVisible] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [sortOrder, setSortOrder] = useState(localStorage.getItem('bank_statement_sort_order') || 'desc');
    const [form] = Form.useForm();

    useEffect(() => {
        fetchData();
        fetchDirectors();
        fetchAccounts();
    }, []);

    useEffect(() => {
        localStorage.setItem('bank_statement_sort_order', sortOrder);
    }, [sortOrder]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/bank-statement');
            if (res.data.success) {
                setData(Array.isArray(res.data.data) ? res.data.data : []);
            }
        } catch (error) {
            message.error('ไม่สามารถดึงข้อมูลได้');
        } finally {
            setLoading(false);
        }
    };

    const fetchDirectors = async () => {
        try {
            const res = await api.get('/api/director-advance/directors');
            if (res.data.success) {
                setDirectors(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching directors:', error);
        }
    };

    const fetchAccounts = async () => {
        try {
            const res = await api.get('/api/director-advance/accounts');
            if (res.data.success) {
                setAccounts(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    };

    const fetchSuggestions = async (record) => {
        try {
            const type = record.debit > 0 ? 'debit' : 'credit';
            const amount = record.debit > 0 ? record.debit : record.credit;
            const res = await api.get(`/api/bank-statement/suggestions?type=${type}&amount=${amount}&date=${dayjs(record.trans_date).format('YYYY-MM-DD')}`);
            if (res.data.success) {
                setSuggestions(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching suggestions:', error);
        }
    };

    const stats = useMemo(() => {
        let totalDeposit = 0;
        let totalWithdraw = 0;
        let depositCount = 0;
        let withdrawCount = 0;

        data.forEach(item => {
            const debit = parseFloat(item.debit) || 0;
            const credit = parseFloat(item.credit) || 0;

            if (credit > 0) {
                totalDeposit += credit;
                depositCount++;
            }
            if (debit > 0) {
                totalWithdraw += debit;
                withdrawCount++;
            }
        });

        return { totalDeposit, totalWithdraw, depositCount, withdrawCount };
    }, [data]);

    const sortedData = useMemo(() => {
        return [...data].sort((a, b) => {
            const dateA = dayjs(`${dayjs(a.trans_date).format('YYYY-MM-DD')} ${a.trans_time || '00:00:00'}`);
            const dateB = dayjs(`${dayjs(b.trans_date).format('YYYY-MM-DD')} ${b.trans_time || '00:00:00'}`);

            if (sortOrder === 'desc') {
                return dateB.unix() - dateA.unix();
            } else {
                return dateA.unix() - dateB.unix();
            }
        });
    }, [data, sortOrder]);

    const handleUpload = async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        setUploading(true);
        try {
            const res = await api.post('/api/bank-statement/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.success) {
                message.success(`นำเข้าข้อมูลสำเร็จ ${res.data.count} รายการ`);
                fetchData();
            } else {
                message.error(res.data.message || 'นำเข้าข้อมูลไม่สำเร็จ');
            }
        } catch (error) {
            message.error('เกิดข้อผิดพลาดในการอัปโหลด');
        } finally {
            setUploading(false);
        }
        return false; // Prevent auto upload by antd
    };

    const handleMappingSubmit = async (values) => {
        try {
            const payload = {
                ...values,
                trans_date: values.trans_date.format('YYYY-MM-DD'),
                statement_id: selectedRow?.id
            };

            let res;
            if (isEditing && selectedRow?.mapped_id) {
                res = await api.put(`/api/director-advance/transactions/${selectedRow.mapped_id}`, payload);
            } else {
                res = await api.post('/api/director-advance/transactions', payload);
            }

            if (res.data.success) {
                message.success(isEditing ? 'แก้ไขรายการเรียบร้อย' : 'บันทึกรายการเงินทดรองจ่ายเรียบร้อย');
                setIsMappingModalVisible(false);
                form.resetFields();
                fetchData();
            }
        } catch (error) {
            message.error('ไม่สามารถบันทึกรายการได้');
        }
    };

    const openMappingModal = (record) => {
        setSelectedRow(record);
        setIsEditing(false);
        setSuggestions([]);
        fetchSuggestions(record);

        const amount = record.debit > 0 ? record.debit : record.credit;
        // If bank withdrawal (debit) -> could be advance (took money) or clear (paid expense)
        // If bank deposit (credit) -> could be return (returned money)
        const type = record.debit > 0 ? 'advance' : 'return';

        form.setFieldsValue({
            trans_date: dayjs(record.trans_date),
            amount: amount,
            description: record.description + (record.remark ? ` - ${record.remark}` : ''),
            type: type,
            ref_no: '',
            director_id: undefined,
            related_account_id: undefined
        });
        setIsMappingModalVisible(true);
    };

    const openEditModal = async (record) => {
        setSelectedRow(record);
        setIsEditing(true);
        setSuggestions([]);
        fetchSuggestions(record);

        try {
            const res = await api.get(`/api/director-advance/transactions/detail/${record.mapped_id}`);
            if (res.data.success) {
                const t = res.data.data;
                const amount = t.debit > 0 ? t.debit : t.credit;
                // Mapping back from advance_transactions to modal type
                let type = 'advance';
                if (t.credit > 0) {
                    // If it was a credit in advance_transactions, it's either clear or return
                    // We'll default to clear if it's not explicitly return, but here we just need a default
                    type = 'clear';
                }

                form.setFieldsValue({
                    director_id: t.director_id,
                    trans_date: dayjs(t.trans_date),
                    type: t.debit > 0 ? 'advance' : 'clear', // Default to clear if credit
                    amount: amount,
                    ref_no: t.ref_no,
                    description: t.description,
                    related_account_id: t.related_account_id
                });
                setIsMappingModalVisible(true);
            }
        } catch (error) {
            message.error('ไม่สามารถดึงข้อมูลรายการได้');
        }
    };

    const columns = [
        {
            title: 'วันที่',
            dataIndex: 'trans_date',
            key: 'trans_date',
            render: (text) => dayjs(text).format('DD/MM/YYYY'),
            sorter: (a, b) => dayjs(a.trans_date).unix() - dayjs(b.trans_date).unix(),
        },
        {
            title: 'เวลา',
            dataIndex: 'trans_time',
            key: 'trans_time',
        },
        {
            title: 'รายการ',
            dataIndex: 'description',
            key: 'description',
        },
        {
            title: 'ถอนเงิน (Debit)',
            dataIndex: 'debit',
            key: 'debit',
            align: 'right',
            render: (val) => val > 0 ? <Text type="danger">{val.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text> : '-',
            sorter: (a, b) => (a.debit || 0) - (b.debit || 0),
        },
        {
            title: 'ฝากเงิน (Credit)',
            dataIndex: 'credit',
            key: 'credit',
            align: 'right',
            render: (val) => val > 0 ? <Text type="success">{val.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text> : '-',
            sorter: (a, b) => (a.credit || 0) - (b.credit || 0),
        },
        {
            title: 'คงเหลือ',
            dataIndex: 'balance',
            key: 'balance',
            align: 'right',
            render: (val) => <Text strong>{val.toLocaleString(undefined, { minimumFractionDigits: 2 })}</Text>,
            sorter: (a, b) => (a.balance || 0) - (b.balance || 0),
        },
        {
            title: 'เลขที่เอกสาร',
            dataIndex: 'mapped_ref_no',
            key: 'mapped_ref_no',
            render: (text) => {
                if (!text) return '-';
                try {
                    if (text.startsWith('[') || text.startsWith('{')) {
                        const tags = JSON.parse(text);
                        if (Array.isArray(tags)) {
                            return (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                    {tags.map((tag, i) => <Tag color="blue" key={i}>{tag}</Tag>)}
                                </div>
                            );
                        }
                    }
                } catch (e) { }
                return <Tag color="blue">{text}</Tag>;
            },
        },
        {
            title: 'ช่องทาง',
            dataIndex: 'channel',
            key: 'channel',
            width: 100,
            ellipsis: true,
        },
        {
            title: 'รายละเอียดเพิ่มเติม',
            dataIndex: 'remark',
            key: 'remark',
            ellipsis: true,
        },
        {
            title: 'จัดการ',
            key: 'action',
            render: (_, record) => record.mapped_id ? (
                <span>
                    <Tag color="success">ลงบัญชีแล้ว</Tag>
                    <Button type="link" onClick={() => openEditModal(record)}>แก้ไข</Button>
                </span>
            ) : (
                <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={() => openMappingModal(record)}>
                    ลงเงินทดรอง
                </Button>
            )
        }
    ];

    return (
        <Layout style={{ padding: '24px' }}>
            <Title level={2}>นำเข้า Statement (KBank)</Title>

            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Card bordered={false} className="stat-card">
                        <Statistic
                            title="ยอดฝากรวม"
                            value={stats.totalDeposit}
                            precision={2}
                            valueStyle={{ color: '#3f8600' }}
                            prefix={<ArrowUpOutlined />}
                            suffix="บาท"
                        />
                        <Text type="secondary">จำนวน {stats.depositCount} รายการ</Text>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bordered={false} className="stat-card">
                        <Statistic
                            title="ยอดถอนรวม"
                            value={stats.totalWithdraw}
                            precision={2}
                            valueStyle={{ color: '#cf1322' }}
                            prefix={<ArrowDownOutlined />}
                            suffix="บาท"
                        />
                        <Text type="secondary">จำนวน {stats.withdrawCount} รายการ</Text>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bordered={false} className="stat-card">
                        <Statistic
                            title="ส่วนต่าง"
                            value={stats.totalDeposit - stats.totalWithdraw}
                            precision={2}
                            valueStyle={{ color: (stats.totalDeposit - stats.totalWithdraw) >= 0 ? '#3f8600' : '#cf1322' }}
                            prefix={<SwapOutlined />}
                            suffix="บาท"
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bordered={false} className="stat-card">
                        <Statistic
                            title="รายการทั้งหมด"
                            value={data.length}
                            suffix="รายการ"
                        />
                    </Card>
                </Col>
            </Row>

            <Card>
                <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
                    <Col span={8}>
                        <Upload
                            beforeUpload={handleUpload}
                            showUploadList={false}
                            accept=".csv"
                        >
                            <Button icon={<UploadOutlined />} loading={uploading} type="primary">
                                อัปโหลดไฟล์ CSV
                            </Button>
                        </Upload>
                    </Col>
                    <Col span={8} style={{ textAlign: 'center' }}>
                        <span style={{ marginRight: 8 }}>เรียงตามวันที่:</span>
                        <Select
                            value={sortOrder}
                            onChange={setSortOrder}
                            style={{ width: 180 }}
                        >
                            <Select.Option value="desc">ใหม่ไปเก่า (Newest)</Select.Option>
                            <Select.Option value="asc">เก่าไปใหม่ (Oldest)</Select.Option>
                        </Select>
                    </Col>
                    <Col span={8} style={{ textAlign: 'right' }}>
                        <Button icon={<ReloadOutlined />} onClick={fetchData}>
                            รีเฟรช
                        </Button>
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={sortedData}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        pageSize: 20,
                        showSizeChanger: true,
                        showTotal: (total) => `ทั้งหมด ${total} รายการ`,
                        position: ['bottomRight']
                    }}
                />
            </Card>

            <Modal
                title={isEditing ? "แก้ไขรายการเงินทดรองจ่าย" : "ลงรายการเงินทดรองจ่ายกรรมการ"}
                open={isMappingModalVisible}
                onCancel={() => setIsMappingModalVisible(false)}
                onOk={() => form.submit()}
                okText="บันทึก"
                cancelText="ยกเลิก"
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={handleMappingSubmit}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="director_id" label="กรรมการ" rules={[{ required: true, message: 'กรุณาเลือกกรรมการ' }]}>
                                <Select placeholder="เลือกกรรมการ">
                                    {directors.map(d => (
                                        <Select.Option key={d.id} value={d.id}>{d.name}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="trans_date" label="วันที่" rules={[{ required: true, message: 'กรุณาเลือกวันที่' }]}>
                                <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="type" label="ประเภทรายการ" rules={[{ required: true, message: 'กรุณาเลือกประเภทรายการ' }]}>
                                <Select>
                                    <Select.Option value="advance">ถอนเงินทดรอง (Advance)</Select.Option>
                                    <Select.Option value="clear">เคลียร์ค่าใช้จ่าย (Clear Expense)</Select.Option>
                                    <Select.Option value="return">คืนเงินบริษัท (Return Cash)</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="amount" label="จำนวนเงิน" rules={[{ required: true, message: 'กรุณาระบุจำนวนเงิน' }]}>
                                <Input type="number" step="0.01" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item label="รายการแนะนำ (จาก Expenses/Receipts)">
                        <Select
                            placeholder="เลือกรายการที่ตรงกันเพื่อดึงเลขที่เอกสาร"
                            onChange={(val) => {
                                const currentRefs = form.getFieldValue('ref_no') || [];
                                if (Array.isArray(currentRefs)) {
                                    if (!currentRefs.includes(val)) {
                                        form.setFieldsValue({ ref_no: [...currentRefs, val] });
                                    }
                                } else {
                                    form.setFieldsValue({ ref_no: [val] });
                                }
                            }}
                            allowClear
                        >
                            {suggestions.map((s, idx) => (
                                <Select.Option key={idx} value={s.ref_no}>
                                    {s.ref_no} - {s.description} ({parseFloat(s.amount).toLocaleString()} บาท)
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="ref_no" label="เลขที่เอกสาร">
                        <Select mode="tags" placeholder="กรอกเลขที่เอกสาร หรือเลือกจากรายการแนะนำด้านบน" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item name="description" label="รายละเอียด">
                        <Input.TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="related_account_id" label="บัญชีที่เกี่ยวข้อง">
                        <Select showSearch optionFilterProp="children" placeholder="เลือกบัญชี">
                            {accounts.map(acc => (
                                <Select.Option key={acc.id} value={acc.id}>{acc.code} - {acc.name}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default BankStatementImportPage;
